import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog as MuiDialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    TextField,
    Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import analyticsService from '../services/AnalyticsService';
import HelpButton from './HelpButton';
import chatbotService from '../services/ChatbotService';

const C = {
    pageBg: '#f8f9fb',
    cardBg: '#ffffff',
    ink: '#1a1a2e',
    secondary: '#5a607f',
    muted: '#8b8fa3',
    border: '#e8eaef',
    borderLight: '#f0f1f5',
    blue: '#3b82f6',
    blueSoft: 'rgba(59, 130, 246, 0.08)',
    green: '#16a34a',
    greenSoft: 'rgba(22, 163, 74, 0.08)',
    amber: '#d97706',
    amberSoft: 'rgba(217, 119, 6, 0.08)',
    red: '#dc2626',
    redSoft: 'rgba(220, 38, 38, 0.08)',
    teal: '#0d9488',
    purple: '#7c3aed',
    grey: '#94a3b8',
    greySoft: 'rgba(148, 163, 184, 0.12)',
};

const emptyAnalytics = {
    summary: {
        conversations: 0,
        activeConversations: 0,
        zeroMessageConversations: 0,
        engagementRate: 0,
        totalMessages: 0,
        userMessages: 0,
        assistantMessages: 0,
        systemMessages: 0,
        avgTurnsPerConversation: 0,
        avgTurnsActiveOnly: 0,
        avgMessagesPerConversation: 0,
        avgMessagesActiveOnly: 0,
        avgResponseSeconds: null,
        p90ResponseSeconds: null,
        feedbackCount: 0,
        agentHandoffs: 0,
        ticketsCreated: 0,
    },
    series: [],
    feedbackRatings: {},
    chatbotBreakdown: [],
    partial: false,
    lastUpdated: null,
};

function formatNumber(value) {
    if (value === null || value === undefined) return '—';
    return new Intl.NumberFormat().format(value);
}

function formatSeconds(value) {
    if (value === null || value === undefined) return '—';
    return `${Number(value).toFixed(Number(value) % 1 === 0 ? 0 : 1)}s`;
}

const DeltaBadge = ({ current, previous, reverseColors = false }) => {
    if (previous === null || previous === undefined || previous === 0) {
        return <Typography sx={{ color: C.muted, fontSize: '0.78rem', fontWeight: 600 }}>—</Typography>;
    }
    if (current === null || current === undefined) {
        return <Typography sx={{ color: C.muted, fontSize: '0.78rem', fontWeight: 600 }}>—</Typography>;
    }
    
    const diff = current - previous;
    const percent = (diff / previous) * 100;
    
    if (diff === 0) {
        return <Typography sx={{ color: C.muted, fontSize: '0.78rem', fontWeight: 600 }}>0%</Typography>;
    }
    
    const isPositive = diff > 0;
    const isNegative = diff < 0;
    
    let color = C.muted;
    if (isPositive) color = reverseColors ? C.red : C.green;
    if (isNegative) color = reverseColors ? C.green : C.red;
    
    const sign = isPositive ? '↑' : '↓';
    const prefix = isPositive ? '+' : '';
    
    return (
        <Typography sx={{ color, fontSize: '0.78rem', fontWeight: 600 }}>
            {sign} {prefix}{Math.abs(percent).toFixed(1)}%
        </Typography>
    );
};

function Analytics() {
    const { t } = useTranslation();

    const [chatbots, setChatbots] = useState([]);
    const [selectedChatbots, setSelectedChatbots] = useState([]);
    const [rangeFrom, setRangeFrom] = useState(dayjs().subtract(30, 'day').startOf('day'));
    const [rangeTo, setRangeTo] = useState(dayjs().endOf('day'));
    const [rangeMenuEl, setRangeMenuEl] = useState(null);
    const [openCustom, setOpenCustom] = useState(false);
    const [tempFrom, setTempFrom] = useState(rangeFrom);
    const [tempTo, setTempTo] = useState(rangeTo);
    const [analytics, setAnalytics] = useState(emptyAnalytics);
    const [previousAnalytics, setPreviousAnalytics] = useState(emptyAnalytics);
    const [loading, setLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const granularity = useMemo(() => {
        return rangeTo.diff(rangeFrom, 'hour') <= 48 ? 'hour' : 'day';
    }, [rangeFrom, rangeTo]);

    const fetchAnalytics = async () => {
        try {
            setLoading(true);
            const currentStart = rangeFrom.unix();
            const currentEnd = rangeTo.unix();
            const duration = currentEnd - currentStart;
            const previousStart = currentStart - duration;
            const previousEnd = currentStart;

            const chatbotIdParam = selectedChatbots.join(',');
            const [data, prevData] = await Promise.all([
                analyticsService.getAnalytics({
                    startDate: currentStart,
                    endDate: currentEnd,
                    granularity,
                    chatbotId: chatbotIdParam,
                }),
                analyticsService.getAnalytics({
                    startDate: previousStart,
                    endDate: previousEnd,
                    granularity,
                    chatbotId: chatbotIdParam,
                }).catch((e) => {
                    console.warn('Failed to fetch previous period analytics', e);
                    return emptyAnalytics;
                }),
            ]);

            setAnalytics(data || emptyAnalytics);
            setPreviousAnalytics(prevData || emptyAnalytics);
        } catch (error) {
            console.error('Error fetching analytics:', error);
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        chatbotService.getChatbots().then((data) => setChatbots(data || [])).catch(() => {});
    }, []);

    useEffect(() => {
        fetchAnalytics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rangeFrom, rangeTo, selectedChatbots, granularity]);

    const handleChatbotChange = (event) => {
        const value = event.target.value;
        setSelectedChatbots(typeof value === 'string' ? value.split(',').filter(Boolean) : value);
    };

    const applyRange = (from, to) => {
        setRangeFrom(from);
        setRangeTo(to);
        setRangeMenuEl(null);
    };

    const chartTickFormat = (bucket) => {
        if (!bucket) return '';
        return granularity === 'hour' ? dayjs(bucket).format('MM/DD HH:mm') : dayjs(bucket).format('MM/DD');
    };

    const tooltipLabelFormat = (bucket) => {
        if (!bucket) return '';
        return granularity === 'hour' ? dayjs(bucket).format('YYYY/MM/DD HH:mm') : dayjs(bucket).format('YYYY/MM/DD');
    };

    const summary = analytics.summary || emptyAnalytics.summary;
    const prevSummary = previousAnalytics.summary || emptyAnalytics.summary;
    const series = analytics.series || [];
    const feedbackRows = Object.entries(analytics.feedbackRatings || {})
        .map(([rating, count]) => ({ rating, count }))
        .sort((a, b) => b.rating.localeCompare(a.rating));
    const allChatbotsSelected = chatbots.length > 0 && selectedChatbots.length === chatbots.length;

    const insights = [];
    if (summary.engagementRate < 0.5 && summary.conversations > 0) {
        insights.push({
            type: 'warning',
            color: C.amber,
            bgColor: C.amberSoft,
            text: t('analytics.insight.lowEngagement', '{{rate}}% of conversations have zero messages — consider improving welcome experience', { rate: Math.round((1 - summary.engagementRate) * 100) })
        });
    }
    if (summary.avgResponseSeconds > 10) {
        insights.push({
            type: 'warning',
            color: C.amber,
            bgColor: C.amberSoft,
            text: t('analytics.insight.slowResponse', 'Avg response time is {{time}} — check model performance', { time: formatSeconds(summary.avgResponseSeconds) })
        });
    }
    if (summary.feedbackCount === 0 && summary.conversations > 0) {
        insights.push({
            type: 'info',
            color: C.secondary,
            bgColor: C.borderLight,
            text: t('analytics.insight.noFeedback', 'No feedback received in this period')
        });
    }

    const getEngagementColor = (rate) => {
        if (rate >= 0.7) return C.green;
        if (rate >= 0.4) return C.amber;
        return C.red;
    };

    const getEngagementBgColor = (rate) => {
        if (rate >= 0.7) return C.greenSoft;
        if (rate >= 0.4) return C.amberSoft;
        return C.redSoft;
    };

    const outcomesData = [
        { name: t('analytics.activeConversations', 'Active'), value: summary.activeConversations, color: C.blue },
        { name: t('analytics.emptyStarts', 'Empty starts'), value: summary.zeroMessageConversations, color: '#f87171' },
        { name: t('analytics.handoffs', 'Handoffs'), value: summary.agentHandoffs, color: C.amber },
    ].filter(d => d.value > 0);

    const maxConversations = Math.max(...(analytics.chatbotBreakdown || []).map(b => b.conversations || 0), 1);

    return (
        <Box
            sx={{
                mx: -3,
                mt: -3,
                mb: -3,
                width: 'calc(100% + 48px)',
                px: { xs: 2, md: 5 },
                pt: 4,
                pb: 4,
                backgroundColor: C.pageBg,
                minHeight: '100vh',
            }}
        >
            {/* Header Bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
                <Box sx={{ mr: 'auto' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, color: C.ink, lineHeight: 1.2 }}>
                            {t('sideBar.nanalytics', 'Analytics')}
                        </Typography>
                        <HelpButton />
                    </Box>
                    <Typography sx={{ mt: 0.5, color: C.muted, fontSize: '0.875rem' }}>
                        {analytics.lastUpdated
                            ? `${t('analytics.lastUpdated', 'Last updated')} ${dayjs.unix(analytics.lastUpdated).format('YYYY/MM/DD HH:mm')}`
                            : t('analytics.liveComputed', 'Live computed from conversation data')}
                    </Typography>
                </Box>

                <FormControl size="small">
                    <Select
                        multiple
                        value={selectedChatbots}
                        onChange={handleChatbotChange}
                        displayEmpty
                        renderValue={(selected) => {
                            if (selected.length === 0) return t('analytics.allChatbots', 'All Chatbots');
                            if (selected.length === 1) {
                                const bot = chatbots.find((item) => item.chatbotId === selected[0]);
                                return bot?.chatbotName || selected[0];
                            }
                            return `${selected.length} ${t('analytics.chatbotsSelected', 'chatbots selected')}`;
                        }}
                        sx={{ minWidth: 220, backgroundColor: C.cardBg, borderRadius: '4px' }}
                    >
                        <MenuItem
                            dense
                            onMouseDown={(event) => {
                                event.preventDefault();
                                setSelectedChatbots(allChatbotsSelected ? [] : chatbots.map((bot) => bot.chatbotId));
                            }}
                        >
                            <Checkbox
                                size="small"
                                checked={allChatbotsSelected}
                                indeterminate={selectedChatbots.length > 0 && selectedChatbots.length < chatbots.length}
                            />
                            <ListItemText
                                primary={allChatbotsSelected ? t('analytics.deselectAll', 'Deselect All') : t('analytics.selectAll', 'Select All')}
                                primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
                            />
                        </MenuItem>
                        <Divider sx={{ my: 0.5 }} />
                        {chatbots.map((bot) => (
                            <MenuItem key={bot.chatbotId} value={bot.chatbotId} dense>
                                <Checkbox size="small" checked={selectedChatbots.includes(bot.chatbotId)} />
                                <ListItemText primary={bot.chatbotName || bot.chatbotId} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Button
                    variant="outlined"
                    size="small"
                    onClick={(event) => setRangeMenuEl(event.currentTarget)}
                    sx={{ minHeight: 40, textTransform: 'none', borderRadius: '4px', borderColor: C.border, color: C.ink, backgroundColor: C.cardBg }}
                >
                    {`${rangeFrom.format('YYYY/MM/DD')} - ${rangeTo.format('YYYY/MM/DD')}`}
                </Button>
                <Menu anchorEl={rangeMenuEl} open={Boolean(rangeMenuEl)} onClose={() => setRangeMenuEl(null)}>
                    <MenuItem onClick={() => applyRange(dayjs().startOf('day'), dayjs().endOf('day'))}>
                        {t('conversation.today', 'Today')}
                    </MenuItem>
                    <MenuItem onClick={() => applyRange(dayjs().subtract(24, 'hour'), dayjs())}>
                        {t('analytics.last24Hours', 'Last 24 hours')}
                    </MenuItem>
                    <MenuItem onClick={() => applyRange(dayjs().subtract(7, 'day').startOf('day'), dayjs().endOf('day'))}>
                        {t('conversation.last7', 'Last 7 days')}
                    </MenuItem>
                    <MenuItem onClick={() => applyRange(dayjs().subtract(30, 'day').startOf('day'), dayjs().endOf('day'))}>
                        {t('conversation.last30', 'Last 30 days')}
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={() => { setTempFrom(rangeFrom); setTempTo(rangeTo); setOpenCustom(true); setRangeMenuEl(null); }}>
                        {t('conversation.custom', 'Custom')}
                    </MenuItem>
                </Menu>

                <Button
                    variant="contained"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={fetchAnalytics}
                    disabled={loading}
                    sx={{ minHeight: 40, textTransform: 'none', borderRadius: '4px', boxShadow: 'none', backgroundColor: C.ink, '&:hover': { backgroundColor: '#000' } }}
                >
                    {t('analytics.refresh', 'Refresh')}
                </Button>
            </Box>

            {analytics.partial && (
                <Alert severity="warning" sx={{ mb: 2.5 }}>
                    {t('analytics.partial', 'This result is partial because the selected range is too large for live analytics. Narrow the date range for complete results.')}
                </Alert>
            )}

            {/* Insights Strip */}
            {insights.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2.5 }}>
                    {insights.map((insight, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', p: 1.5, borderRadius: 1, backgroundColor: insight.bgColor, border: `1px solid ${insight.color}30` }}>
                            <Typography sx={{ color: insight.color, fontSize: '0.875rem', fontWeight: 600 }}>
                                {insight.text}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {/* Primary Metrics Row */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2.5, mb: 2.5 }}>
                {/* Card 1: Conversations */}
                <Paper elevation={0} sx={{ p: 2.5, border: `1px solid ${C.border}`, borderRadius: 1, backgroundColor: C.cardBg, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.06)' } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography sx={{ color: C.secondary, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {t('analytics.conversations', 'Conversations')}
                        </Typography>
                        <DeltaBadge current={summary.conversations} previous={prevSummary.conversations} />
                    </Box>
                    <Typography sx={{ color: C.ink, fontSize: '2rem', fontWeight: 700, mt: 1, mb: 0.5, lineHeight: 1.1 }}>
                        {formatNumber(summary.conversations)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ color: C.muted, fontSize: '0.8rem' }}>
                            {formatNumber(summary.activeConversations)} {t('analytics.active', 'active')} · {formatNumber(summary.zeroMessageConversations)} {t('analytics.empty', 'empty')}
                        </Typography>
                        {summary.conversations > 0 && (
                            <Box sx={{ px: 1, py: 0.25, borderRadius: 1, backgroundColor: getEngagementBgColor(summary.engagementRate), color: getEngagementColor(summary.engagementRate), fontSize: '0.7rem', fontWeight: 700 }}>
                                {Math.round(summary.engagementRate * 100)}% {t('analytics.engagement', 'engagement')}
                            </Box>
                        )}
                    </Box>
                </Paper>

                {/* Card 2: Response Time */}
                <Paper elevation={0} sx={{ p: 2.5, border: `1px solid ${C.border}`, borderRadius: 1, backgroundColor: C.cardBg, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.06)' } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography sx={{ color: C.secondary, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {t('analytics.avgResponse', 'Avg Response Time')}
                        </Typography>
                        <DeltaBadge current={summary.avgResponseSeconds} previous={prevSummary.avgResponseSeconds} reverseColors />
                    </Box>
                    <Typography sx={{ color: C.ink, fontSize: '2rem', fontWeight: 700, mt: 1, mb: 0.5, lineHeight: 1.1 }}>
                        {formatSeconds(summary.avgResponseSeconds)}
                    </Typography>
                    <Typography sx={{ color: C.muted, fontSize: '0.8rem' }}>
                        p90: {formatSeconds(summary.p90ResponseSeconds)}
                    </Typography>
                </Paper>

                {/* Card 3: User Messages */}
                <Paper elevation={0} sx={{ p: 2.5, border: `1px solid ${C.border}`, borderRadius: 1, backgroundColor: C.cardBg, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.06)' } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography sx={{ color: C.secondary, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {t('analytics.userMessages', 'User Messages')}
                        </Typography>
                        <DeltaBadge current={summary.userMessages} previous={prevSummary.userMessages} />
                    </Box>
                    <Typography sx={{ color: C.ink, fontSize: '2rem', fontWeight: 700, mt: 1, mb: 0.5, lineHeight: 1.1 }}>
                        {formatNumber(summary.userMessages)}
                    </Typography>
                    <Typography sx={{ color: C.muted, fontSize: '0.8rem' }}>
                        {summary.avgMessagesActiveOnly} {t('analytics.avgPerActive', 'avg per active conversation')}
                    </Typography>
                </Paper>
            </Box>

            {/* Secondary Metrics Strip */}
            <Paper elevation={0} sx={{ display: 'flex', flexWrap: 'wrap', p: 2, border: `1px solid ${C.border}`, borderRadius: 1, backgroundColor: C.cardBg, mb: 2.5 }}>
                {[
                    { label: t('analytics.feedback', 'Feedback'), value: formatNumber(summary.feedbackCount) },
                    { label: t('analytics.handoffs', 'Handoffs'), value: formatNumber(summary.agentHandoffs) },
                    { label: t('analytics.tickets', 'Tickets'), value: formatNumber(summary.ticketsCreated) },
                    { label: t('analytics.avgTurns', 'Avg Turns (Active)'), value: summary.avgTurnsActiveOnly },
                ].map((item, idx, arr) => (
                    <React.Fragment key={item.label}>
                        <Box sx={{ px: 3, display: 'flex', alignItems: 'baseline', gap: 1 }}>
                            <Typography sx={{ color: C.secondary, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                {item.label}
                            </Typography>
                            <Typography sx={{ color: C.ink, fontSize: '1.1rem', fontWeight: 700 }}>
                                {item.value}
                            </Typography>
                        </Box>
                        {idx < arr.length - 1 && <Divider orientation="vertical" flexItem sx={{ borderColor: C.borderLight }} />}
                    </React.Fragment>
                ))}
            </Paper>

            {/* Conversation Trend — full width */}
            <Paper elevation={0} sx={{ p: 2.5, border: `1px solid ${C.border}`, borderRadius: 1, backgroundColor: C.cardBg, mb: 2.5 }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, mb: 2, color: C.ink }}>
                    {t('analytics.conversationTrend', 'Conversation Trend')}
                </Typography>
                <Box sx={{ height: 280 }}>
                    {series.length === 0 ? (
                        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted }}>
                            <Typography variant="body2">{t('usage.noData', 'No data available')}</Typography>
                        </Box>
                    ) : (
                        <ResponsiveContainer>
                            <AreaChart data={series} margin={{ top: 8, right: 16, bottom: 18, left: 0 }}>
                                <defs>
                                    <linearGradient id="colorConversations" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={C.blue} stopOpacity={0.2}/>
                                        <stop offset="95%" stopColor={C.blue} stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke={C.borderLight} vertical={false} />
                                <XAxis dataKey="bucket" tickFormatter={chartTickFormat} tick={{ fontSize: 11, fill: C.secondary }} tickLine={false} axisLine={{ stroke: C.border }} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: C.secondary }} axisLine={false} tickLine={false} />
                                <Tooltip labelFormatter={tooltipLabelFormat} contentStyle={{ borderRadius: '8px', border: `1px solid ${C.border}`, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }} />
                                <Area type="monotone" dataKey="conversations" name={t('analytics.conversations', 'Conversations')} stroke={C.blue} strokeWidth={2} fillOpacity={1} fill="url(#colorConversations)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </Box>
            </Paper>

            {/* Messages by Role + Outcomes & Feedback combined */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '3fr 2fr' }, gap: 2.5, mb: 2.5 }}>
                <Paper elevation={0} sx={{ p: 2.5, border: `1px solid ${C.border}`, borderRadius: 1, backgroundColor: C.cardBg }}>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, mb: 2, color: C.ink }}>
                        {t('analytics.messagesByRole', 'Messages by Role')}
                    </Typography>
                    <Box sx={{ height: 300 }}>
                        {series.length === 0 ? (
                            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted }}>
                                <Typography variant="body2">{t('usage.noData', 'No data available')}</Typography>
                            </Box>
                        ) : (
                            <ResponsiveContainer>
                                <BarChart data={series} margin={{ top: 8, right: 16, bottom: 18, left: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={C.borderLight} vertical={false} />
                                    <XAxis dataKey="bucket" tickFormatter={chartTickFormat} tick={{ fontSize: 11, fill: C.secondary }} tickLine={false} axisLine={{ stroke: C.border }} />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: C.secondary }} axisLine={false} tickLine={false} />
                                    <Tooltip labelFormatter={tooltipLabelFormat} contentStyle={{ borderRadius: '8px', border: `1px solid ${C.border}`, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }} />
                                    <Legend verticalAlign="top" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px', color: C.secondary }} />
                                    <Bar dataKey="userMessages" stackId="messages" name={t('analytics.userMessages', 'User')} fill={C.blue} radius={[0, 0, 0, 0]} />
                                    <Bar dataKey="assistantMessages" stackId="messages" name={t('analytics.assistantMessages', 'Assistant')} fill={C.teal} />
                                    <Bar dataKey="systemMessages" stackId="messages" name={t('analytics.systemMessages', 'System')} fill={C.purple} fillOpacity={0.35} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </Box>
                </Paper>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
                    <Paper elevation={0} sx={{ p: 2.5, border: `1px solid ${C.border}`, borderRadius: 1, backgroundColor: C.cardBg }}>
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, mb: 1, color: C.ink }}>
                            {t('analytics.conversationOutcomes', 'Conversation Outcomes')}
                        </Typography>
                        <Box sx={{ height: 200 }}>
                            {outcomesData.length === 0 ? (
                                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted }}>
                                    <Typography variant="body2">{t('usage.noData', 'No data available')}</Typography>
                                </Box>
                            ) : (
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie
                                            data={outcomesData}
                                            cx="50%"
                                            cy="46%"
                                            innerRadius={44}
                                            outerRadius={68}
                                            paddingAngle={3}
                                            dataKey="value"
                                            stroke="none"
                                        >
                                            {outcomesData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip contentStyle={{ borderRadius: '8px', border: `1px solid ${C.border}`, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }} />
                                        <Legend verticalAlign="bottom" height={28} iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '11px', color: C.secondary }} />
                                        <text x="50%" y="42%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: '18px', fontWeight: 700, fill: C.ink }}>
                                            {formatNumber(summary.conversations)}
                                        </text>
                                        <text x="50%" y="53%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: '10px', fill: C.muted }}>
                                            {t('analytics.total', 'Total')}
                                        </text>
                                    </PieChart>
                                </ResponsiveContainer>
                            )}
                        </Box>
                    </Paper>

                    <Paper elevation={0} sx={{ p: 2.5, border: `1px solid ${C.border}`, borderRadius: 1, backgroundColor: C.cardBg, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, mb: 1.5, color: C.ink }}>
                            {t('analytics.feedbackSummary', 'Feedback Summary')}
                        </Typography>
                        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            {feedbackRows.length === 0 ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted }}>
                                    <Typography variant="body2">{t('analytics.noFeedback', 'No feedback received')}</Typography>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {feedbackRows.map((row) => {
                                        const maxCount = Math.max(...feedbackRows.map(r => r.count));
                                        const percentage = (row.count / maxCount) * 100;
                                        return (
                                            <Box key={row.rating} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Typography sx={{ minWidth: 70, fontSize: '0.8rem', fontWeight: 600, color: C.ink }}>
                                                    {row.rating}
                                                </Typography>
                                                <Box sx={{ flexGrow: 1, height: 6, backgroundColor: C.borderLight, borderRadius: 3, overflow: 'hidden' }}>
                                                    <Box sx={{ width: `${percentage}%`, height: '100%', backgroundColor: C.green, borderRadius: 3 }} />
                                                </Box>
                                                <Typography sx={{ minWidth: 28, textAlign: 'right', fontSize: '0.8rem', fontWeight: 600, color: C.secondary }}>
                                                    {formatNumber(row.count)}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </Box>
            </Box>

            {/* Chatbot Performance Table */}
            <Paper elevation={0} sx={{ p: 2.5, border: `1px solid ${C.border}`, borderRadius: 1, backgroundColor: C.cardBg }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, mb: 2, color: C.ink }}>
                    {t('analytics.chatbotPerformance', 'Chatbot Performance')}
                </Typography>
                <Box sx={{ overflowX: 'auto' }}>
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
                        <Box component="thead">
                            <Box component="tr" sx={{ '& th': { color: C.secondary, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', textAlign: 'left', pb: 1.5, borderBottom: `1px solid ${C.border}` } }}>
                                <Box component="th">{t('analytics.chatbot', 'Chatbot')}</Box>
                                <Box component="th">{t('analytics.conversations', 'Conversations')}</Box>
                                <Box component="th">{t('analytics.engagementRate', 'Engagement')}</Box>
                                <Box component="th">{t('analytics.messages', 'Messages')}</Box>
                                <Box component="th">{t('analytics.avgTurns', 'Avg Turns')}</Box>
                                <Box component="th">{t('analytics.feedback', 'Feedback')}</Box>
                            </Box>
                        </Box>
                        <Box component="tbody">
                            {(analytics.chatbotBreakdown || []).map((bot) => {
                                const convPercent = (bot.conversations / maxConversations) * 100;
                                const botEngagement = bot.conversations > 0 ? ((bot.conversations - (bot.zeroMessageConversations || 0)) / bot.conversations) : 0;
                                return (
                                    <Box key={bot.chatbotId} component="tr" sx={{ '&:hover': { backgroundColor: C.pageBg }, transition: 'background-color 0.2s', '& td': { py: 1.5, borderBottom: `1px solid ${C.borderLight}`, fontSize: '0.875rem', verticalAlign: 'middle' } }}>
                                        <Box component="td">
                                            <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: C.ink }}>{bot.chatbotName || bot.chatbotId}</Typography>
                                            <Typography sx={{ color: C.muted, fontSize: '0.75rem' }}>{bot.vendor || bot.chatbotId}</Typography>
                                        </Box>
                                        <Box component="td" sx={{ minWidth: 140 }}>
                                            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', mb: 0.5 }}>{formatNumber(bot.conversations)}</Typography>
                                            <Box sx={{ height: 4, backgroundColor: C.borderLight, borderRadius: 2, overflow: 'hidden', width: '100%', maxWidth: 120 }}>
                                                <Box sx={{ width: `${convPercent}%`, height: '100%', backgroundColor: C.blue, borderRadius: 2, transition: 'width 0.3s ease' }} />
                                            </Box>
                                        </Box>
                                        <Box component="td">
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', px: 1, py: 0.25, borderRadius: 1, backgroundColor: getEngagementBgColor(botEngagement) }}>
                                                <Typography sx={{ color: getEngagementColor(botEngagement), fontWeight: 600, fontSize: '0.8rem' }}>
                                                    {Math.round(botEngagement * 100)}%
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box component="td">{formatNumber(bot.totalMessages)}</Box>
                                        <Box component="td">{bot.avgTurnsPerConversation}</Box>
                                        <Box component="td">{formatNumber(bot.feedbackCount)}</Box>
                                    </Box>
                                );
                            })}
                            {(analytics.chatbotBreakdown || []).length === 0 && (
                                <Box component="tr">
                                    <Box component="td" colSpan={6} sx={{ py: 4, textAlign: 'center', color: C.muted }}>
                                        {t('usage.noData', 'No data available')}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Paper>

            <MuiDialog open={openCustom} onClose={() => setOpenCustom(false)}>
                <DialogTitle>{t('conversation.custom', 'Custom range')}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                        <TextField
                            type="date"
                            label={t('conversation.from', 'From')}
                            InputLabelProps={{ shrink: true }}
                            value={tempFrom.format('YYYY-MM-DD')}
                            onChange={(event) => {
                                if (event.target.value) setTempFrom(dayjs(event.target.value).startOf('day'));
                            }}
                        />
                        <TextField
                            type="date"
                            label={t('conversation.to', 'To')}
                            InputLabelProps={{ shrink: true }}
                            value={tempTo.format('YYYY-MM-DD')}
                            onChange={(event) => {
                                if (event.target.value) setTempTo(dayjs(event.target.value).endOf('day'));
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenCustom(false)}>{t('conversation.cancel', 'Cancel')}</Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            applyRange(tempFrom, tempTo);
                            setOpenCustom(false);
                        }}
                    >
                        {t('conversation.apply', 'Apply')}
                    </Button>
                </DialogActions>
            </MuiDialog>

            <MuiDialog open={loading} PaperProps={{ sx: { borderRadius: 1, minWidth: 280, textAlign: 'center', p: 2 } }}>
                <DialogContent>
                    <CircularProgress size={28} sx={{ mb: 2 }} />
                    <Typography>{t('analytics.loading', 'Loading analytics...')}</Typography>
                </DialogContent>
            </MuiDialog>

            <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setSnackbarOpen(false)} sx={{ width: '100%' }}>
                    {t('analytics.loadError', 'Failed to load analytics')}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default Analytics;
